// Add-to-quotable pipeline for compatible combinations, extracted verbatim
// from the CCB modal (AHA-6681 CS-4) so the ducted detail panel's "Add to
// Comparison" button can share it. Same house pattern as
// utils/combination-pricing: plain functions here, types imported from the
// modal via `import type` (erased at compile time — no runtime cycle).
import type { AppDispatch } from 'app/store';
import type {
  BasicProductData,
  EnrichedAccessory
} from 'components/layout/modals/compatible-combinations-browser-modal';
import type {
  ExistingSystemComponentType,
  SelectedSystemType,
  SizingBandsSettingType
} from 'data/types';
import { addSelectedSystem } from 'features/systems';
import axios from 'utils/api';
import { planCompatibilityPositions } from 'utils/compatibility-positions';
import {
  resolveCombinationProducts,
  toCombinationPayload
} from 'utils/compatibility-quotable-products';
import type {
  CompatibilityConfigurationResponse,
  CompatibilityQuotableSystemsResponse
} from 'utils/compatibility-quotable-types';
import { buildSelectedSystemFromCompatibility } from 'utils/compatibility-systems';
import {
  AccessoryGroup,
  AccessoryPick,
  AccessoryPicks,
  groupCombinationAccessories,
  hasAccessoryChoice,
  picksByTypeCode,
  resolveAccessoriesString
} from 'utils/ducted-accessory-groups';
import type { DuctedCoverageType } from 'utils/ducted-coverage';
import { mergeDuctedCoverage } from 'utils/ducted-coverage';
import type {
  DuctedBinCoverage,
  DuctedOperatingCost
} from 'utils/ducted-system-metrics';
import {
  readDuctedBinCoverage,
  readDuctedDesignCoverage,
  readDuctedOperatingCost
} from 'utils/ducted-system-metrics';
import { resolveOpenSlotPlaceholderLabels } from 'utils/open-slot-placeholder-labels';
import { errorModal } from 'utils/ui';

// Shape of the per-row payload built from the table's checkbox selection.
// Mirrors the inferred type of `selectedCombinations` and is reused by the
// accessory-selection modal flow so combinations can be snapshotted, mutated
// (to filter multi-option accessories), and replayed through
// `addCombinationsToQuotable`. `productId` is typed as `number` because every
// selectable combination has passed `canSelectCombination` (which guarantees
// each real slot resolves to a productId); the `?? null` in
// `buildSelectedCombination` is a runtime safety net.
export type SelectedCombination = {
  combinationId: number;
  systemTypeLabel: string;
  outdoor: {
    manufacturer: string;
    modelNumber: string;
    productId: number;
  } | null;
  indoor: {
    manufacturer: string;
    modelNumber: string;
    productId: number;
  } | null;
  blower: {
    manufacturer: string;
    modelNumber: string;
    productId: number;
  } | null;
  seer2: number | null;
  eer2: number | null;
  hspf2: number | null;
  cop5: number | null;
  coolingCapacity: number | null;
  heatingCapacity: number | null;
  afue: number | null;
  accessories: string | null;
  // Kept-constrained result-set marker (AHA-6681 sub-build 4): carried into
  // the manufactured system's tags so the "Pairs with Customer's Equipment"
  // distinction survives into the comparison and quote artifacts. Absent on
  // CCB-modal selections (single-set browse context).
  keptPairing?: boolean;
  // Kept-aware "Customer's <equipment>" display label (AHA-6681 display
  // titles): becomes the manufactured system's default `name`. Grouping and
  // the Phase 5/6 POST bodies keep reading the raw systemTypeLabel.
  displayName?: string;
  // Numbers lifted off the combination row so the manufactured system carries
  // the same operating cost and performance figures the user saw on the card.
  // Absent when the row had none of them.
  operatingCost?: DuctedOperatingCost | null;
  binCoverage?: DuctedBinCoverage | null;
  designCoverage?: DuctedCoverageType | null;
};

// Shape of one combination's multi-option accessory groups. Each entry in
// `combinationsWithChoices` (passed to AccessorySelectionModal) follows this
// type — only types with >1 option are included.
export type ComboNeedingSelection = {
  combinationId: number;
  systemTypeLabel: string;
  accessoryGroups: AccessoryGroup[];
};

// A combination is selectable only when every non-Generic, non-null position
// resolves to a productId in the Phase 3 light enrichment payload.
export function canSelectCombination(
  combination: any,
  productData: Record<string, BasicProductData>
): boolean {
  const positions = [
    {
      mfr: combination.OutdoorManufacturer,
      model: combination.OutdoorModelNumber
    },
    {
      mfr: combination.IndoorManufacturer,
      model: combination.IndoorModelNumber
    },
    {
      mfr: combination.BlowerManufacturer,
      model: combination.BlowerModelNumber
    }
  ];
  for (const pos of positions) {
    if (pos.mfr && pos.mfr !== 'Generic' && pos.model) {
      const key = `${pos.mfr}|${pos.model}`;
      if (!productData[key]?.productId) return false;
    }
  }
  return true;
}

// Shaped payload for the Phase 5 bulk-add endpoint. Generic positions are
// dropped to null; real positions carry the productId resolved by Phase 3.
export function buildSelectedCombination(
  c: any,
  productData: Record<string, BasicProductData>,
  keptPairing?: boolean,
  displayName?: string
): SelectedCombination {
  const operatingCost = readDuctedOperatingCost(c);
  const binCoverage = readDuctedBinCoverage(c);
  const designCoverage = readDuctedDesignCoverage(c);
  return {
    ...(keptPairing ? { keptPairing } : {}),
    ...(displayName ? { displayName } : {}),
    ...(operatingCost ? { operatingCost } : {}),
    ...(binCoverage ? { binCoverage } : {}),
    ...(designCoverage ? { designCoverage } : {}),
    combinationId: c.ID,
    systemTypeLabel: c.SystemTypeLabel,
    outdoor:
      c.OutdoorManufacturer && c.OutdoorManufacturer !== 'Generic'
        ? {
            manufacturer: c.OutdoorManufacturer,
            modelNumber: c.OutdoorModelNumber,
            productId:
              productData[`${c.OutdoorManufacturer}|${c.OutdoorModelNumber}`]
                ?.productId ?? null
          }
        : null,
    indoor:
      c.IndoorManufacturer && c.IndoorManufacturer !== 'Generic'
        ? {
            manufacturer: c.IndoorManufacturer,
            modelNumber: c.IndoorModelNumber,
            productId:
              productData[`${c.IndoorManufacturer}|${c.IndoorModelNumber}`]
                ?.productId ?? null
          }
        : null,
    blower:
      c.BlowerManufacturer && c.BlowerManufacturer !== 'Generic'
        ? {
            manufacturer: c.BlowerManufacturer,
            modelNumber: c.BlowerModelNumber,
            productId:
              productData[`${c.BlowerManufacturer}|${c.BlowerModelNumber}`]
                ?.productId ?? null
          }
        : null,
    seer2: c.SEER2,
    eer2: c.EER2,
    hspf2: c.HSPF2,
    cop5: c.COP5,
    coolingCapacity: c.CoolingCapacity,
    heatingCapacity: c.HeatingCapacity,
    afue: c.AFUE,
    // Raw STRING_AGG ("17*7,5*1") passed through so Phase 3 can persist the
    // combination's required accessories. The Phase 6 endpoint ignores
    // unknown fields today.
    accessories: (c.Accessories as string | null | undefined) ?? null
  };
}

// End-to-end add flow for a (possibly accessory-filtered) set of selected
// combinations:
//   Phase 5 (create configuration + position rows)
//     → Phase 6 (create QuotableSystems + enrich products)
//     → manufacture SelectedSystemType client-side
//     → dispatch addSelectedSystem
//     → onSuccess(added) for caller-specific side-effects
//
// Selections are grouped by systemTypeLabel because Phase 5 takes a single
// label per call; users browsing without a system-type filter may select
// combinations across multiple labels in one go, and each label maps to its
// own configuration on the backend. Errors stay in-module via errorModal;
// onSuccess only fires when every group landed.
export async function addCombinationsToQuotable({
  combos,
  instanceUniqueId,
  components,
  existingSystemUniqueId,
  tonnageSeed,
  calculatedLoads,
  sizingBands,
  dispatch,
  onSuccess
}: {
  combos: SelectedCombination[];
  instanceUniqueId: string | null;
  components: ExistingSystemComponentType[];
  // The existing system `components` belong to. Stamped on every manufactured
  // system so the quote-time ducted layout can read that system's placed
  // equipment positions (see utils/ducted-layout-configuration).
  existingSystemUniqueId?: string;
  // The GUI-resolved tonnage seed (shared resolveTonnageSeed on the same
  // Redux inputs the CCB-check dispatchers use). Forwarded to Phase 6 so
  // the server can compute tonnage-estimate ducted coverage when the
  // structure has no completed sizing (AHA-6796). Null = no seed.
  tonnageSeed: { min: number; max: number } | null;
  // The project's calculated totals (heating, and cooling with latent gain
  // folded in). They lead the server's requirement ladder, so sending them
  // is what makes a saved system's coverage match the card it came from —
  // the combinations fetch already sends the same numbers, and the server
  // labels both from the structure the calculator persisted them on.
  calculatedLoads: {
    heating: number;
    cooling: number;
  };
  // The hydrated 'sizingBands' setting (AHA-6796): required — not optional —
  // so every consumer decides (the tonnageSeed convention). Snapshotted onto
  // each built ducted zone as sizingThresholds so the quote's match labels
  // reflect the bands it was filtered with. Null (older API deploy / invalid
  // record) attaches nothing → legacy labels downstream.
  sizingBands: SizingBandsSettingType | null;
  dispatch: AppDispatch;
  onSuccess: (added: SelectedSystemType[]) => void;
}): Promise<void> {
  if (combos.length === 0) return;
  if (!instanceUniqueId) {
    errorModal('Cannot add systems: project instance not loaded.');
    return;
  }

  // Phase 5 positions come from the existing system's components (both
  // replacing and keeping, so the configuration's equipment tables capture
  // the full installed picture) plus a "new addition" position for every
  // slot the selected combinations fill that no component covers.
  const { positions, keptComponentLabels, keptBlower, keptSlots } =
    planCompatibilityPositions(components, combos);

  if (positions.length === 0) {
    errorModal(
      'Cannot add systems: no recognized outdoor/indoor/blower components on the existing system.'
    );
    return;
  }

  // Group selections by their system-type label so each group maps to a
  // single Phase 5 configuration.
  const groups = new Map<string, SelectedCombination[]>();
  for (const sc of combos) {
    const existing = groups.get(sc.systemTypeLabel) ?? [];
    existing.push(sc);
    groups.set(sc.systemTypeLabel, existing);
  }

  try {
    const added: SelectedSystemType[] = [];
    // Sequential per group: Phase 5 → Phase 6 is a chain (Phase 6 needs the
    // configurationUniqueId / equipmentRecords from Phase 5), and we want
    // the configurations to land in a stable order.
    // eslint-disable-next-line no-restricted-syntax
    for (const [systemTypeLabel, groupCombos] of groups) {
      // Phase 5: create (or reuse) the configuration.
      // eslint-disable-next-line no-await-in-loop
      const configResponse = await axios.post(
        '/compatibility-quotable-systems/configuration',
        {
          instanceUniqueId,
          systemTypeLabel,
          positions,
          keptComponentLabels
        }
      );
      const {
        configurationUniqueId,
        label: configurationLabel,
        equipmentRecords
      } = configResponse.data as CompatibilityConfigurationResponse;

      // Phase 6: persist the QuotableSystem rows for this group and return
      // enrichment + performance data for client-side manufacture.
      // eslint-disable-next-line no-await-in-loop
      const qsResponse = await axios.post('/compatibility-quotable-systems', {
        configurationUniqueId,
        combinations: groupCombos.map(toCombinationPayload),
        equipmentRecords,
        tonnageSeed,
        totalCoolingBTU: calculatedLoads.cooling,
        totalHeatingBTU: calculatedLoads.heating
      });
      // The project-level confidence score (AHA-6796) arrives once per
      // request and is stamped on each built system below.
      const { quotableSystems, confidence } =
        qsResponse.data as CompatibilityQuotableSystemsResponse;

      // Zip input combinations with persisted quotable systems by index.
      // The Phase 6 use-case preserves input ordering, so this is the
      // contract that links each manufactured object back to its source row.
      groupCombos.forEach((combo, i) => {
        const qs = quotableSystems[i];
        if (!qs) return;

        // A non-null combination slot means a product is being purchased
        // (replacing existing equipment or a new addition); null slots are
        // kept equipment. Required accessories persisted by Phase 3 ride
        // back on the per-system products list.
        const {
          replacingProductIds,
          outdoorProduct,
          indoorProduct,
          blowerProduct,
          accessories
        } = resolveCombinationProducts(combo, qs);

        const openSlotLabels = resolveOpenSlotPlaceholderLabels({
          combo,
          systemTypeLabel: combo.systemTypeLabel,
          keptSlots,
          keptBlower
        });

        const selectedSystem = buildSelectedSystemFromCompatibility({
          quotableSystemUniqueId: qs.uniqueId,
          configurationUniqueId,
          configurationLabel,
          systemTypeLabel: combo.systemTypeLabel,
          combinationPerformance: qs.combinationPerformance,
          outdoorProduct,
          indoorProduct,
          blowerProduct,
          replacingProductIds,
          ...openSlotLabels,
          keptPairing: combo.keptPairing,
          displayName: combo.displayName,
          ductedCoverage: mergeDuctedCoverage(
            qs.ductedCoverage,
            combo.designCoverage
          ),
          operatingCost: combo.operatingCost ?? null,
          binCoverage: combo.binCoverage ?? null,
          confidence: confidence ?? null,
          sizingBands,
          accessories,
          existingSystemUniqueId,
          compatibilityCombinationId: combo.combinationId
        });

        dispatch(addSelectedSystem(selectedSystem));
        added.push(selectedSystem);
      });
    }

    onSuccess(added);
  } catch (error) {
    console.error('Failed to add compatibility systems to quotable:', error);
    errorModal(
      error instanceof Error
        ? `Failed to add systems: ${error.message}`
        : 'Failed to add systems to quotable. Please try again.'
    );
  }
}

export function applyAccessoryPicks(
  combos: SelectedCombination[],
  accessoryData: Record<string, EnrichedAccessory>,
  picks: AccessoryPicks
): SelectedCombination[] {
  return combos.map((sc) => ({
    ...sc,
    accessories: resolveAccessoriesString(sc.accessories, accessoryData, picks)
  }));
}

// Detection pass for the add flow's entry point. When any combination has
// multi-option accessory types (e.g. several heat kits), the caller defers to
// the AccessorySelectionModal; the user picks exactly one per type and the
// combinations' accessories strings are rebuilt (rebuildCombinationAccessories)
// before calling `addCombinationsToQuotable`. Combinations with 0–1 accessory
// per type, or none at all, produce no entry here.
export function findCombosNeedingSelection(
  combos: SelectedCombination[],
  accessoryData: Record<string, EnrichedAccessory>
): ComboNeedingSelection[] {
  return combos
    .map((sc): ComboNeedingSelection | null => {
      const accessoryGroups = groupCombinationAccessories(
        sc.accessories,
        accessoryData
      ).filter(hasAccessoryChoice);
      if (accessoryGroups.length === 0) return null;
      return {
        combinationId: sc.combinationId,
        systemTypeLabel: sc.systemTypeLabel,
        accessoryGroups
      };
    })
    .filter((c): c is ComboNeedingSelection => c !== null);
}

// Called once the user has picked one accessory per multi-option type in the
// AccessorySelectionModal. Resolves each combination's `accessories`
// STRING_AGG through the same resolver as applyAccessoryPicks so the backend
// only persists the user's choice (and the default option of every type the
// picker did not show); the result is handed to the existing Phase 5/6 flow.
export function rebuildCombinationAccessories(
  pending: SelectedCombination[],
  selections: Map<number, AccessoryPick[]>,
  accessoryData: Record<string, EnrichedAccessory>
): SelectedCombination[] {
  return pending.map((sc) => ({
    ...sc,
    accessories: resolveAccessoriesString(
      sc.accessories,
      accessoryData,
      picksByTypeCode(selections.get(sc.combinationId) ?? [], accessoryData)
    )
  }));
}
